/*
 * Reusable modal component
 *
 * To use this modal, wrap two main elements inside the <fp-modal></fp-modal> tag:
 * 1. A trigger element with the "data-fp-modal-trigger" attribute
 * 2. A modal content element with the "data-fp-modal-content" attribute at the top level
 *
 * Example:
 * // Don't forget the scripts
 * {{ 'fp-modal.css' | asset_url | stylesheet_tag }}
 * <script src="{{ 'fp-modal.js' | asset_url }}" defer="defer"></script>
 *
 * <fp-modal>
 *   <button data-fp-modal-trigger>
 *     {% render 'icon-info' %}
 *   </button>
 *   <div data-fp-modal-content>
 *     <h1>Hello world!</h1>
 *     <p>This will be in the modal</p>
 *   </div>
 * </fp-modal>
 */
class FPModal extends HTMLElement {
  constructor() {
    super();
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleFocusTrap = this.handleFocusTrap.bind(this);
    this.previousActiveElement = null;
  }

  connectedCallback() {
    this.modalTrigger = this.querySelector("[data-fp-modal-trigger]");
    this.modalContent = this.querySelector("[data-fp-modal-content]");
    this.contentClone = this.modalContent?.cloneNode(true);
    this.modalId = crypto.randomUUID() || Date.now().toString(16);
    this.backdropId = this.modalId + "-backdrop";
    this.closeBtnId = this.modalId + "-close-btn";

    this.modalContent?.remove();
    this.modalNode = this.createModal();
    document.body.appendChild(this.modalNode);

    this.modal = document.getElementById(this.modalId);
    this.backdrop = document.getElementById(this.backdropId);
    this.closeBtn = document.getElementById(this.closeBtnId);

    this.modalTrigger?.addEventListener("click", this.open);
    this.backdrop?.addEventListener("click", this.close);
    this.closeBtn?.addEventListener("click", this.close);
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnectedCallback() {
    this.modalTrigger?.removeEventListener("click", this.open);
    this.backdrop?.removeEventListener("click", this.close);
    this.closeBtn?.removeEventListener("click", this.close);
    document.removeEventListener("keydown", this.handleKeydown);
    this.modal?.remove();
  }

  createModal() {
    const html = (strings, ...values) => String.raw({ raw: strings }, ...values);

    const modal = document.createElement("div");
    modal.classList.add("fp-modal__container");
    modal.setAttribute("id", this.modalId);
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.innerHTML = html`
      <div class="fp-modal__backdrop" id="${this.backdropId}"></div>
      <div class="fp-modal__content-wrapper">
        <button aria-label="Close modal" class="fp-modal__close-btn" id="${this.closeBtnId}">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="icon icon-close"
            fill="none"
            width="18"
            height="17"
            viewBox="0 0 18 17"
            aria-hidden="true"
          >
            <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
          </svg>
        </button>
        <div class="fp-modal__content">${this.contentClone?.outerHTML}</div>
      </div>
    `;
    return modal;
  }

  handleKeydown(event) {
    if (!this.modal.classList.contains("active")) return;

    if (event.key === "Escape") {
      this.close();
    }

    if (event.key === "Tab") {
      this.handleFocusTrap(event);
    }
  }

  handleFocusTrap(event) {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length > 1 ? focusableElements.length - 1 : 0];

    if (event.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    }
  }

  open() {
    document.body.style.overflow = "hidden";
    this.previousActiveElement = document.activeElement;

    this.modal.classList.add("active");
    this.modal.setAttribute("aria-hidden", "false");

    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    //wait for animation to complete
    setTimeout(() => {
      if (focusableElements.length) {
        focusableElements[0].focus();
      }
    }, 500);
  }

  close() {
    document.body.style.overflow = "auto";
    this.modal.classList.add("hide");
    this.modal.setAttribute("aria-hidden", "true");

    // Restore focus to the previous element
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    setTimeout(() => {
      this.modal.classList.remove("active");
      this.modal.classList.remove("hide");
    }, 550);
  }
}

customElements.define("fp-modal", FPModal);
